import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, Spinner } from 'react-bootstrap'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsCalendarCheck, BsCreditCard2Front, BsPeople } from 'react-icons/bs'
import 'bootstrap/dist/css/bootstrap.min.css'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { useSubscriptionNeedPlan } from './commonComponents/SubscriptionNeedPlanContext'

const STATUS_CONFIG = {
  active: { label: 'Active', bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  expired: { label: 'Expired', bg: '#FEF2F2', color: '#991B1B', dot: '#EF4444' },
  cancelled: { label: 'Cancelled', bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' },
  pending: { label: 'Pending', bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
}

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || {
    label: status ? String(status).replace(/_/g, ' ') : 'Unknown',
    bg: '#F3F4F6',
    color: '#374151',
    dot: '#9CA3AF',
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.3px',
        background: cfg.bg,
        color: cfg.color,
        fontFamily: 'Inter, system-ui, sans-serif',
        textTransform: 'capitalize',
      }}
    >
      <span
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  )
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatPaiseAsInr = (amountInPaise, currency = 'INR') => {
  if (amountInPaise == null || amountInPaise === '') return '-'
  const amountInRupees = Number(amountInPaise) / 100
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0,
  }).format(amountInRupees)
}

const formatBillingCycle = (cycle) => {
  if (!cycle) return '-'
  return String(cycle).replace(/_/g, ' ')
}

const DetailItem = ({ label, value, children }) => (
  <div className="col-md-6 col-lg-4">
    <label
      style={{
        fontSize: '11px',
        color: '#6b7280',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'block',
        marginBottom: '6px',
      }}
    >
      {label}
    </label>
    {children || (
      <p
        style={{
          margin: 0,
          fontSize: '15px',
          color: '#111827',
          fontWeight: 600,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {value ?? '-'}
      </p>
    )}
  </div>
)

const AdminSubscription = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 992 : false,
  )
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [marginLeft, setMarginLeft] = useState('0px')
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState(null)
  const [error, setError] = useState('')
  const paymentRedirectHandled = useRef(false)

  const { needPlan } = useSubscriptionNeedPlan()
  const isAdminUser =
    user?.user_type === 'admin' || (user?.roles || []).includes('admin')
  const canAccessSubscription = isAdminUser || needPlan

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
        setIsDesktop(true)
      } else {
        setMarginLeft('0px')
        setIsDesktop(false)
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchSubscription = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true)
      setError('')
    }
    try {
      const response = await ApiEndpoints.getCurrentSubscription()
      setSubscription(response.data?.data || null)
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Failed to load subscription details. Please try again.'
      if (!silent) {
        setError(message)
        setSubscription(null)
        toast.error(message)
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!canAccessSubscription) return

    const activatedSubscription = location.state?.subscription
    if (activatedSubscription) {
      paymentRedirectHandled.current = true
      setSubscription(activatedSubscription)
      setError('')
      setLoading(false)
      navigate('/admin/subscription', { replace: true, state: null })
      fetchSubscription({ silent: true })
      return
    }

    if (paymentRedirectHandled.current) {
      paymentRedirectHandled.current = false
      return
    }

    fetchSubscription()
  }, [canAccessSubscription, fetchSubscription, location.pathname, location.state, navigate])

  if (!canAccessSubscription) {
    return <Navigate to="/Dashboard" replace />
  }

  const plan = subscription?.plan || {}

  return (
    <div
      style={{
        marginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
        marginTop: '0px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingBottom: 24,
      }}
    >
      <div
        className="container-fluid p-0"
        style={{
          marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          paddingLeft: '0px',
          paddingRight: '0px',
        }}
      >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%',
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  background: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {!logoError ? (
                  <img
                    src={logo}
                    alt="School Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>

              <h3
                className="m-0 fw-bold"
                style={{
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Subscription
              </h3>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/subscription/plans')}
                style={{
                  height: isMobile ? 'auto' : '40px',
                  minHeight: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#223843',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '2px' : '6px',
                  padding: isMobile ? '6px 10px' : '0 14px',
                  fontWeight: 600,
                  fontSize: isMobile ? '10px' : '13px',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                <BsCreditCard2Front size={isMobile ? 14 : 16} />
                View Plans
              </button>
              <button
                type="button"
                onClick={fetchSubscription}
                disabled={loading}
                title="Refresh"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <BsArrowClockwise size={18} />
              </button>
              {!isMobile && <ProfileDropdown />}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          boxSizing: 'border-box',
          paddingLeft: isDesktop ? (isCollapsed ? 32 : 40) : 16,
          paddingRight: isDesktop ? 32 : 16,
        }}
      >
        {loading ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '48px', textAlign: 'center' }}
          >
            <Spinner animation="border" variant="secondary" />
            <p
              className="mt-3 mb-0"
              style={{ color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Loading subscription details...
            </p>
          </Card>
        ) : error && !subscription ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '32px', textAlign: 'center' }}
          >
            <p style={{ color: '#991B1B', marginBottom: '16px' }}>{error}</p>
            <button
              type="button"
              onClick={fetchSubscription}
              style={{
                background: '#223843',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontWeight: 600,
              }}
            >
              Try Again
            </button>
          </Card>
        ) : !subscription ? (
          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: '12px', padding: '32px', textAlign: 'center' }}
          >
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              No active subscription found.
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin/subscription/plans')}
              style={{
                background: '#223843',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 18px',
                fontWeight: 600,
              }}
            >
              Browse Plans
            </button>
          </Card>
        ) : (
          <>
            <Card
              className="border-0 shadow-sm mb-4"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #E5E7EB',
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #223843 0%, #2f4f5c 100%)',
                  color: '#ffffff',
                  padding: '24px 28px',
                }}
              >
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '12px',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        opacity: 0.85,
                      }}
                    >
                      Current Plan
                    </p>
                    <h4
                      className="mb-1"
                      style={{
                        fontWeight: 700,
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {plan.name || 'Subscription Plan'}
                    </h4>
                    <p className="mb-0" style={{ opacity: 0.9, fontSize: '14px' }}>
                      {plan.description || 'Institute subscription'}
                    </p>
                  </div>
                  <StatusBadge status={subscription.status} />
                </div>
              </div>

              <Card.Body style={{ padding: '28px' }}>
                <div className="row g-4">
                  <DetailItem label="Amount" value={formatPaiseAsInr(subscription.amount)} />
                  <DetailItem
                    label="Billing Cycle"
                    value={formatBillingCycle(plan.billing_cycle)}
                  />
                  <DetailItem
                    label="Duration"
                    value={
                      subscription.duration_months
                        ? `${subscription.duration_months} month${
                            subscription.duration_months > 1 ? 's' : ''
                          }`
                        : '-'
                    }
                  />
                  <DetailItem
                    label="Student Limit"
                    value={
                      subscription.student_limit != null
                        ? `${subscription.student_limit} students`
                        : '-'
                    }
                  />
                  <DetailItem label="Starts On" value={formatDate(subscription.starts_at)} />
                  <DetailItem label="Ends On" value={formatDate(subscription.ends_at)} />
                  <DetailItem
                    label="Days Remaining"
                    value={
                      subscription.days_remaining != null
                        ? `${subscription.days_remaining} day${
                            subscription.days_remaining === 1 ? '' : 's'
                          }`
                        : '-'
                    }
                  />
                  <DetailItem label="Usable" value={subscription.is_usable ? 'Yes' : 'No'} />
                </div>
              </Card.Body>
            </Card>

            <div className="row g-3">
              <div className="col-md-4">
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}
                >
                  <Card.Body className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#EFF6FF',
                        color: '#1D4ED8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BsCreditCard2Front size={20} />
                    </div>
                    <div>
                      <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                        Plan Amount
                      </p>
                      <p className="mb-0 fw-bold" style={{ color: '#111827' }}>
                        {formatPaiseAsInr(subscription.amount)}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="col-md-4">
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}
                >
                  <Card.Body className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#ECFDF5',
                        color: '#065F46',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BsPeople size={20} />
                    </div>
                    <div>
                      <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                        Student Capacity
                      </p>
                      <p className="mb-0 fw-bold" style={{ color: '#111827' }}>
                        {subscription.student_limit ?? '-'}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="col-md-4">
                <Card
                  className="border-0 shadow-sm h-100"
                  style={{ borderRadius: '12px', border: '1px solid #E5E7EB' }}
                >
                  <Card.Body className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#FFFBEB',
                        color: '#92400E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BsCalendarCheck size={20} />
                    </div>
                    <div>
                      <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                        Days Remaining
                      </p>
                      <p className="mb-0 fw-bold" style={{ color: '#111827' }}>
                        {subscription.days_remaining ?? '-'}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AdminSubscription
